import { Link } from "@tanstack/react-router";
import { rotatedWindow } from "@/lib/link-rotation";

import { ALL_DATA_TYPES, getDataTypeInfo } from "@/lib/vehicle-data";
import type { RelatedLinks as RelatedLinksData } from "@/lib/vehicle-queries";
import { TOPICS, topicForDataType } from "@/lib/topics-config";
import { isValidDataType } from "@/lib/vehicle-data";

interface Props {
  makeName: string;
  makeSlug: string;
  modelName?: string;
  modelSlug?: string;
  related: RelatedLinksData;
  /** Current leaf data type, used to surface its topic index first. */
  dataType?: string;
  /** Same-attribute pages for other models of this make. */
  attributeSiblings?: { name: string; slug: string }[];
  /** Same attribute on other brands, used when this make has few models. */
  crossMakeSiblings?: { name: string; slug: string; makeName: string; makeSlug: string }[];
  /** Anchors the rotating sibling window so every model gets linked. */
  currentModelSlug?: string;
  /** False for models with no EPA rows — their EPA-derived leaf pages 404. */
  hasEpaData?: boolean;
}

/** Internal link hub rendered at the bottom of make/model/spec pages. */
export function RelatedLinks({
  makeName,
  makeSlug,
  modelName,
  modelSlug,
  related,
  dataType,
  attributeSiblings,
  crossMakeSiblings,
  currentModelSlug,
  hasEpaData = true,
}: Props) {
  const primary = dataType ? topicForDataType(dataType) : undefined;
  const topics = primary
    ? [primary, ...TOPICS.filter((t) => t.slug !== primary.slug)]
    : TOPICS;
  const attrLabel =
    dataType && isValidDataType(dataType) ? getDataTypeInfo(dataType).shortLabel : "";
  // Always slicing the first 20 siblings starved models late in the alphabet of
  // inbound links. The current model is filtered out upstream, so an index
  // lookup can't anchor the window either — rotatedWindow falls back to a hash
  // of the path, which keeps the output stable per URL while giving every
  // model, including those on one- and two-model brands, its share of links.
  const siblings = attributeSiblings ?? [];
  const siblingWindow = rotatedWindow(
    siblings,
    20,
    currentModelSlug ?? modelSlug ?? makeSlug,
    (s) => s.slug,
  );

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10 border-t border-border">
      {dataType && siblingWindow.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-3">
            {attrLabel} for other {makeName} models
          </h2>
          <ul className="flex flex-wrap gap-2">
            {siblingWindow.map((m) => (
              <li key={m.slug}>
                <Link
                  to="/$make/$model/$dataType"
                  params={{ make: makeSlug, model: m.slug, dataType }}
                  className="chip-link"
                >
                  {makeName} {m.name} {attrLabel}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {dataType && crossMakeSiblings && crossMakeSiblings.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-3">
            {attrLabel} on other brands
          </h2>
          <ul className="flex flex-wrap gap-2">
            {crossMakeSiblings.map((m) => (
              <li key={`${m.makeSlug}-${m.slug}`}>
                <Link
                  to="/$make/$model/$dataType"
                  params={{ make: m.makeSlug, model: m.slug, dataType }}
                  className="chip-link"
                >
                  {m.makeName} {m.name} {attrLabel}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}


      <div className="mb-8">
        <h2 className="text-lg font-semibold text-foreground mb-3">
          Compare across every brand
        </h2>
        <ul className="flex flex-wrap gap-2">
          {topics.map((t) => (
            <li key={t.slug}>
              <Link
                to={t.path}
                className="chip-link"
              >
                {t.h1}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/maintenance-comparison"
              className="chip-link"
            >
              Maintenance Intervals by Brand
            </Link>
          </li>
        </ul>
      </div>

      {modelSlug && modelName && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-3">
            All {makeName} {modelName} specifications
          </h2>
          <ul className="flex flex-wrap gap-2">
            {ALL_DATA_TYPES.filter(
              (dt) => hasEpaData || dt === "maintenance-schedule",
            ).map((dt) => {
              const info = getDataTypeInfo(dt);
              return (
                <li key={dt}>
                  <Link
                    to="/$make/$model/$dataType"
                    params={{ make: makeSlug, model: modelSlug, dataType: info.slug }}
                    className="chip-link"
                  >
                    {makeName} {modelName} {info.shortLabel}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {related.siblingModels.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-3">
            More {makeName} models
          </h2>
          <ul className="flex flex-wrap gap-2">
            {related.siblingModels.map((m) => (
              <li key={m.slug}>
                <Link
                  to="/$make/$model"
                  params={{ make: makeSlug, model: m.slug }}
                  className="chip-link"
                >
                  {makeName} {m.name}
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/$make"
            params={{ make: makeSlug }}
            className="mt-3 inline-block text-sm text-primary hover:underline"
          >
            View all {makeName} models →
          </Link>
        </div>
      )}

      {related.otherMakes.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-3">
            Browse other makes
          </h2>
          <ul className="flex flex-wrap gap-2">
            {related.otherMakes.map((m) => (
              <li key={m.slug}>
                <Link
                  to="/$make"
                  params={{ make: m.slug }}
                  className="chip-link"
                >
                  {m.name} specs
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
